import React, { useEffect, useState } from 'react';
import { Input, Button, Alert, Spin, Tag, Switch, Tooltip } from 'antd';
import { NotificationOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { getUser, can } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';

const { TextArea } = Input;

const CLES = {
  PRODUCTION: 'BANNIERE_TEXTE_PRODUCTION',
  TEST: 'BANNIERE_TEXTE_TEST',
};

/* UNE VALEUR A TROIS ETATS, DERRIERE DEUX INTERRUPTEURS. La regle est que les
   deux environnements ne sont jamais actifs ensemble : avec deux booleens, cet
   etat interdit existerait et il faudrait le defendre ; avec une valeur unique,
   il n'est pas representable. L'ecran, lui, garde deux interrupteurs. */
const CLE_ACTIVE = 'BANNIERE_ACTIVE';
const AUCUN = 'AUCUN';

/**
 * Textes du bandeau d'environnement.
 *
 * CE QUI SE RÈGLE ICI, ET CE QUI NE S'Y RÈGLE PAS. L'AFFICHAGE du bandeau et les
 * deux textes, oui. Le MODE, non : il vient de la propriété serveur
 * `app.environnement`, posée au déploiement. Seul l'interrupteur du mode déclaré
 * par ce serveur est donc manipulable, l'autre reste grisé avec son motif.
 *
 * Le rendre modifiable ici aurait ouvert la porte à l'accident que ce bandeau
 * existe pour empêcher : un serveur de test annonçant « PRODUCTION » après une
 * restauration de dump, ou l'inverse.
 *
 * LES DEUX TEXTES SONT ÉDITABLES, PAS SEULEMENT CELUI DU MODE COURANT. On rédige
 * rarement le texte de production depuis la production : on le prépare sur le
 * serveur de test, où l'on travaille. N'exposer que le texte actif aurait rendu
 * l'autre inatteignable.
 */
export default function ParametresBanniere() {
  const { t } = useTranslation('parametres');
  const user = getUser();
  const habilite = can(HAB.PARAMETRE_UPDATE, user) && can(HAB.PARAMETRE_CREATE, user);

  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [environnement, setEnvironnement] = useState(null);
  const [existants, setExistants] = useState({});
  const [textes, setTextes] = useState({ PRODUCTION: '', TEST: '' });
  const [actif, setActif] = useState(AUCUN);
  const [bascule, setBascule] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setChargement(true);
      try {
        const [banniere, parametres] = await Promise.all([
          axiosInstance.get('/v1/banniere', { skipErrorToast: true }),
          axiosInstance.get('/v1/parametres'),
        ]);
        if (ignore) return;

        setEnvironnement(banniere?.data?.data?.environnement ?? null);
        setActif(banniere?.data?.data?.actif ?? AUCUN);

        const tous = parametres?.data?.data || [];
        const trouver = (cle) => tous.find((p) => p.cle === cle) || null;
        const prod = trouver(CLES.PRODUCTION);
        const test = trouver(CLES.TEST);
        setExistants({ PRODUCTION: prod, TEST: test, ACTIF: trouver(CLE_ACTIVE) });
        setTextes({ PRODUCTION: prod?.valeur || '', TEST: test?.valeur || '' });
      } catch {
        if (!ignore) toast.error(i18n.t('parametres:banniere.messages.chargementImpossible'));
      } finally {
        if (!ignore) setChargement(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const enregistrer = async () => {
    if (!habilite) return;
    setEnregistrement(true);
    try {
      const aEcrire = [
        { mode: 'PRODUCTION', cle: CLES.PRODUCTION, valeur: textes.PRODUCTION.trim(),
          description: 'Texte du bandeau affiche quand app.environnement vaut PRODUCTION' },
        { mode: 'TEST', cle: CLES.TEST, valeur: textes.TEST.trim(),
          description: 'Texte du bandeau affiche quand app.environnement vaut TEST' },
      ];
      await Promise.all(aEcrire.map(({ mode, cle, valeur, description }) => {
        const payload = { typeParametre: 'TEXTE', cle, valeur, description };
        const existant = existants[mode];
        return existant
          ? axiosInstance.put(`/v1/parametres/${existant.parametreTrackingId}`, payload)
          : axiosInstance.post('/v1/parametres', payload);
      }));
      toast.success(t('banniere.messages.textesEnregistres'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('banniere.messages.echecEnregistrement'));
    } finally {
      setEnregistrement(false);
    }
  };

  /* UN SEUL PARAMETRE POUR DEUX INTERRUPTEURS. Allumer un mode ecrit ce mode ;
     eteindre l'actif ecrit AUCUN. L'exclusion mutuelle n'a pas a etre verifiee :
     la valeur ne peut pas porter deux modes a la fois. */
  const basculer = async (mode, allume) => {
    if (!habilite) return;
    const valeur = allume ? mode : AUCUN;
    setBascule(true);
    try {
      const payload = {
        typeParametre: 'TEXTE', cle: CLE_ACTIVE, valeur,
        description: "Affichage du bandeau d'environnement : TEST, PRODUCTION ou AUCUN",
      };
      const existant = existants.ACTIF;
      const res = existant
        ? await axiosInstance.put(`/v1/parametres/${existant.parametreTrackingId}`, payload)
        : await axiosInstance.post('/v1/parametres', payload);

      // Relu du serveur et non pose localement : c'est lui qui borne le choix,
      // et l'ecran doit montrer ce qui sera REELLEMENT affiche.
      if (!existant) setExistants((e) => ({ ...e, ACTIF: res?.data?.data || null }));
      const b = await axiosInstance.get('/v1/banniere', { skipErrorToast: true });
      setActif(b?.data?.data?.actif ?? AUCUN);

      toast.success(allume
        ? t('banniere.messages.bandeauActive')
        : t('banniere.messages.bandeauDesactive'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('banniere.messages.echecMiseAJour'));
    } finally {
      setBascule(false);
    }
  };

  /**
   * Un environnement, un bloc : son interrupteur, puis son texte.
   *
   * POURQUOI LES DEUX ENSEMBLE. Séparés, on réglait l'affichage en haut de page
   * et l'on rédigeait le texte plus bas, sans que rien ne relie les deux : on
   * pouvait allumer un bandeau sans voir ce qu'il allait annoncer. Réunis, le
   * texte se lit à l'endroit même où l'on décide de l'afficher.
   */
  const blocEnvironnement = (mode, titre, aide) => {
    const autorise = environnement === mode;
    const commande = (
      <Switch
        checked={actif === mode}
        disabled={!habilite || !autorise}
        loading={bascule}
        onChange={(v) => basculer(mode, v)}
        checkedChildren={t('banniere.bloc.actif')}
        unCheckedChildren={t('banniere.bloc.inactif')}
      />
    );

    return (
      <div className="cb-card" style={{ padding: 20, marginTop: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600, color: '#111827' }}>{titre}</span>
              {autorise && <Tag color="green" style={{ margin: 0 }}>{t('banniere.bloc.environnementCourant')}</Tag>}
            </div>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, maxWidth: 560 }}>
              {autorise
                ? t('banniere.bloc.autorise')
                : t('banniere.bloc.nonAutorise', { environnement: environnement || t('banniere.bloc.inconnu') })}
            </div>
          </div>
          {autorise ? commande : (
            <Tooltip title={t('banniere.bloc.reserve', { mode })}>
              <span>{commande}</span>
            </Tooltip>
          )}
        </div>

        {/* Le texte reste modifiable même quand l'interrupteur est interdit : on
            rédige rarement le texte de production depuis la production, on le
            prépare sur le serveur de test, où l'on travaille. */}
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 6, maxWidth: 620 }}>
            {aide}
          </div>
          <TextArea
            rows={3}
            value={textes[mode]}
            disabled={!habilite}
            maxLength={400}
            showCount
            onChange={(e) => setTextes((t) => ({ ...t, [mode]: e.target.value }))}
          />
        </div>
      </div>
    );
  };

  return (
    <Layout title={t('banniere.page.titreOnglet')}>
      <div className="cb-page-header">
        <h1 className="cb-page-title">
          <NotificationOutlined style={{ color: '#1a6b3c', marginRight: 8 }} />
          {t('banniere.page.titre')}
        </h1>
        <p className="cb-page-sub">
          {t('banniere.page.sousTitre')}
        </p>
      </div>

      {chargement ? <Spin /> : (
        <>
          <Alert
            type="info"
            showIcon
            message={environnement
              ? t('banniere.alerte.mode', { environnement })
              : t('banniere.alerte.modeIlisible')}
            description={
              <Trans t={t} i18nKey="banniere.alerte.description" components={{ 1: <b />, 3: <b /> }} />
            }
            style={{ marginBottom: 4 }}
          />

          {blocEnvironnement('PRODUCTION', t('banniere.environnements.productionTitre'),
            t('banniere.environnements.productionAide'))}

          {blocEnvironnement('TEST', t('banniere.environnements.testTitre'),
            t('banniere.environnements.testAide'))}

          {actif === AUCUN && (
            <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 10 }}>
              {t('banniere.environnements.aucunActif')}
            </div>
          )}

          {!habilite && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message={t('banniere.lectureSeule.titre')}
              description={t('banniere.lectureSeule.description')}
            />
          )}

          <div style={{ marginTop: 16 }}>
            <Button
              type="primary"
              loading={enregistrement}
              disabled={!habilite}
              onClick={enregistrer}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            >
              {t('banniere.actions.enregistrer')}
            </Button>
          </div>
        </>
      )}
    </Layout>
  );
}
